import { io, Socket } from 'socket.io-client';
import { GameMode, OnlineRoomState, OnlinePlayer, Die, YatzyScores } from '../types/game';

interface ServerPlayer {
  id: string;
  name: string;
  isHost: boolean;
  isReady: boolean;
  isConnected?: boolean;
  totalScore: number;
  scores: YatzyScores;
  lastActivity: string | number | Date;
  upperSectionScore?: number;
  upperSectionBonus?: number;
}

interface ServerRoom {
  id: string;
  code: string;
  host: string;
  players: ServerPlayer[];
  gameMode: GameMode;
  maxPlayers: number;
  isPrivate: boolean;
  gameState: 'waiting' | 'playing' | 'finished';
  currentPlayerIndex: number;
  currentRound: number;
  dice: Die[];
  rollsLeft: number;
  isRolling: boolean;
  createdAt: string | number | Date;
  lastActivity: string | number | Date;
}

interface RoomEventPayload {
  room: ServerRoom;
  playerId: string;
}

export interface WebSocketEvents {
  'connect': () => void;
  'disconnect': (reason: string) => void;
  'connect_error': (error: Error) => void;
  'room_created': (payload: RoomEventPayload) => void;
  'room_joined': (payload: RoomEventPayload) => void;
  'room_updated': (room: ServerRoom) => void;
  'game_event': (event: Record<string, unknown>) => void;
  'player_message': (message: { playerId: string; playerName: string; message: string; timestamp: string }) => void;
  'error': (error: { message: string; code?: string }) => void;
}

class SocketService {
  private socket: Socket | null = null;
  private serverUrl: string;
  private isConnected: boolean = false;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 5;
  private playerId: string | null = null;

  constructor() {
    const envUrl = (process.env.REACT_APP_SERVER_URL || '').trim();

    if (envUrl) {
      this.serverUrl = envUrl;
      return;
    }

    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        this.serverUrl = 'http://localhost:3081';
      } else {
        this.serverUrl = window.location.origin;
      }
      return;
    }

    this.serverUrl = 'http://localhost:3081';
  }

  connect(): Promise<Socket> {
    return new Promise((resolve, reject) => {
      if (this.socket && this.isConnected) {
        resolve(this.socket);
        return;
      }

      this.socket = io(this.serverUrl, {
        transports: ['websocket', 'polling'],
        timeout: 5000,
        forceNew: true
      });

      this.socket.on('connect', () => {
        console.log('Connected to Yatzy server');
        this.isConnected = true;
        this.reconnectAttempts = 0;
        resolve(this.socket!);
      });

      this.socket.on('disconnect', (reason) => {
        console.log('Disconnected from server:', reason);
        this.isConnected = false;
        
        // Auto-reconnect logic
        if (reason === 'io server disconnect') {
          // Server intentionally disconnected, don't reconnect
          return;
        }
        
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectAttempts++;
          console.log(`Attempting to reconnect... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
          setTimeout(() => this.connect(), 2000 * this.reconnectAttempts);
        }
      });

      this.socket.on('connect_error', (error) => {
        console.error('Connection error:', error);
        this.isConnected = false;
        
        if (this.reconnectAttempts === 0) {
          reject(error);
        }
      });
    });
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.reconnectAttempts = 0;
      this.playerId = null;
    }
  }

  // Event listeners
  on(event: string, listener: (...args: any[]) => void): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }
    this.socket.on(event, listener);
  }

  off(event: string, listener?: (...args: any[]) => void): void {
    if (!this.socket) return;
    this.socket.off(event, listener);
  }

  // Room management
  createRoom(playerName: string, gameMode: GameMode = 'classic', isPrivate: boolean = false, modeSettings: any = {}): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }
    this.socket.emit('create_room', { playerName, gameMode, isPrivate, modeSettings });
  }

  joinRoom(roomId: string, playerName: string): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }
    this.socket.emit('join_room', { roomCode: roomId.toUpperCase(), playerName });
  }

  leaveRoom(): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }
    this.socket.emit('leave_room');
    this.playerId = null;
  }

  // Game actions
  startGame(): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }
    this.socket.emit('start_game');
  }

  toggleReady(): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }
    this.socket.emit('ready_toggle');
  }

  rollDice(): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }
    this.socket.emit('roll_dice');
  }
  
  // Funktion för att återställa tärningarna när en ny spelare börjar
  resetPlayerDice(): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }
    // Används för att säkerställa att alla tärningar återställs vid ny tur
    this.socket.emit('reset_dice');
  }

  lockDie(dieId: number): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }
    this.socket.emit('lock_die', dieId);
  }

  selectScore(category: string): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }
    this.socket.emit('select_score', category);
  }

  // Chat (optional for future)
  sendChatMessage(message: string): void {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }
    this.socket.emit('send_message', message);
  }

  // Utility methods
  isSocketConnected(): boolean {
    return this.isConnected && this.socket?.connected === true;
  }

  getSocketId(): string | undefined {
    return this.socket?.id;
  }

  setPlayerId(playerId: string) {
    this.playerId = playerId;
  }

  getPlayerId(): string | null {
    return this.playerId;
  }

  mapRoom(room: ServerRoom): OnlineRoomState {
    return {
      id: room.id,
      code: room.code,
      host: room.host,
      players: room.players.map((player) => this.mapPlayer(player)),
      gameMode: room.gameMode,
      maxPlayers: room.maxPlayers,
      isPrivate: room.isPrivate,
      gameState: room.gameState,
      currentPlayerIndex: room.currentPlayerIndex ?? 0,
      currentRound: room.currentRound ?? 1,
      dice: room.dice ?? [],
      rollsLeft: room.rollsLeft ?? 3,
      isRolling: room.isRolling ?? false,
      createdAt: this.normaliseDate(room.createdAt),
      lastActivity: this.normaliseDate(room.lastActivity)
    };
  }

  private mapPlayer(player: ServerPlayer): OnlinePlayer {
    return {
      id: player.id,
      name: player.name,
      isHost: player.isHost,
      isReady: player.isReady,
      isConnected: player.isConnected ?? true,
      totalScore: player.totalScore,
      scores: player.scores,
      lastActivity: this.normaliseDate(player.lastActivity),
      upperSectionScore: player.upperSectionScore ?? 0,
      upperSectionBonus: player.upperSectionBonus ?? 0,
    };
  }

  private normaliseDate(value: string | number | Date): string {
    if (value instanceof Date) {
      return value.toISOString();
    }
    if (typeof value === 'number') {
      return new Date(value).toISOString();
    }
    return value;
  }
}

// Export singleton instance
export const socketService = new SocketService();
export default socketService;